"use client";

import React from "react";
import { motion } from "motion/react";
import { HugeiconsIcon } from "@hugeicons/react";
import { 
  CpuIcon, 
  SparklesIcon, 
  CodeIcon, 
  AiSecurityIcon 
} from "@hugeicons/core-free-icons";

const values = [
  {
    name: "Velocity First",
    size: "md:col-span-6 col-span-12",
    icon: CpuIcon,
    color: "from-blue-500/10 to-cyan-500/10 border-blue-500/20 text-blue-600 dark:text-blue-400",
    description:
      "Speed is a metric of product quality. We construct lightweight bundles, intelligent cache systems, and optimized routes to achieve sub-second loading speeds on every viewport.",
  },
  {
    name: "Aesthetic Precision",
    size: "md:col-span-6 col-span-12",
    icon: SparklesIcon,
    color: "from-pink-500/10 to-rose-500/10 border-pink-500/20 text-pink-600 dark:text-pink-400",
    description:
      "Aesthetics define user retention. We detail micro-animations, color systems, and typographic grids with microscopic precision, establishing a premium visual presence.",
  },
  {
    name: "Adaptive Technology",
    size: "md:col-span-6 col-span-12",
    icon: CodeIcon,
    color: "from-amber-500/10 to-orange-500/10 border-amber-500/20 text-amber-600 dark:text-amber-400",
    description:
      "We design system architectures to grow without bloat. By utilizing modular component patterns and AI integration layers, we build codebases that age gracefully.",
  },
  {
    name: "Radical Trust",
    size: "md:col-span-6 col-span-12",
    icon: AiSecurityIcon,
    color: "from-purple-500/10 to-violet-500/10 border-purple-500/20 text-purple-600 dark:text-purple-400",
    description:
      "We believe in transparency. We share real-time progress repositories, absolute architecture plans, and direct communication channels. No surprises, just collaboration.",
  },
];

export default function CoreValue() {
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring" as const,
        stiffness: 140,
        damping: 18,
      },
    },
  };

  return (
    <section className="py-24 relative px-4 flex flex-col justify-center items-center bg-neutral-50 dark:bg-black transition-colors duration-500">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-violet-650/5 dark:bg-violet-950/[0.02] blur-[150px] pointer-events-none" />

      <div className="max-w-5xl w-full z-10 text-center mb-16">
        <motion.div 
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="inline-block px-3 py-1 rounded-full text-xs font-mono font-bold tracking-wider text-purple-650 dark:text-purple-400 bg-purple-500/10 uppercase mb-4"
        >
          Our Core DNA
        </motion.div>
        <motion.h2 
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-4xl font-bold text-slate-900 dark:text-white sm:text-5xl font-recoleta tracking-tight"
        >
          The Principles We Code By
        </motion.h2>
        <motion.p 
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="mt-4 text-lg sm:text-xl text-neutral-600 dark:text-neutral-400 font-manjari font-light max-w-2xl mx-auto leading-relaxed"
        >
          Technology moves fast. Our principles keep us focused on building the highest quality 
          software for our clients.
        </motion.p>
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        className="max-w-5xl w-full grid grid-cols-12 gap-6 z-10"
      >
        {values.map((value) => (
          <motion.div
            key={value.name}
            variants={itemVariants}
            whileHover={{ y: -6, scale: 1.015 }}
            transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
            className={`${value.size} cursor-pointer`}
          >
            <div className="h-full border border-neutral-200/50 dark:border-neutral-800/40 bg-white/70 dark:bg-neutral-900/10 backdrop-blur-xl shadow-lg hover:shadow-xl hover:border-slate-350 dark:hover:border-neutral-700/60 transition-all duration-300 rounded-3xl p-6 sm:p-8 flex flex-col sm:flex-row items-start gap-5">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-gradient-to-br ${value.color}`}>
                <HugeiconsIcon icon={value.icon} className="w-6 h-6" size={24} />
              </div>
              <div className="space-y-2 text-left">
                <h3 className="text-xl font-bold text-slate-800 dark:text-white font-recoleta">
                  {value.name}
                </h3>
                <p className="text-neutral-600 dark:text-neutral-400 text-sm leading-relaxed font-outfit font-medium">
                  {value.description}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}
